import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Home, 
  Package, 
  Users, 
  User, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Bell,
  ChevronDown,
  Search
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './Sidebar';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const navigationItems = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Shipments', href: '/shipments', icon: Package },
    { name: 'Customers', href: '/customers', icon: Users },
    ...(user?.roles && user.roles.some(role => ['management', 'system_administrator'].includes(role.name)) 
      ? [{ name: 'Users', href: '/users', icon: User }] 
      : []
    ),
  ];

  const isActive = (href) => location.pathname === href;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={closeSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            className="fixed left-0 top-0 h-full z-50 lg:hidden"
          >
            <Sidebar onClose={closeSidebar} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-3 py-2 lg:px-4 lg:py-3">
            {/* Mobile Menu Button */}
            <div className="flex items-center lg:hidden">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-1.5 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>

            {/* Logo/Brand */}
            <div className="flex items-center space-x-2 lg:space-x-3">
              <h1 className="text-base lg:text-lg font-semibold text-gray-900 truncate">
                Cargo Management
              </h1>
            </div>
            
            {/* User Menu */}
            <div className="flex items-center space-x-1 lg:space-x-3">
              {/* Notifications */}
              <button className="p-1.5 lg:p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors">
                <Bell className="h-4 w-4 lg:h-5 lg:w-5" />
              </button>

              {/* User Profile */}
              <div className="flex items-center space-x-1.5 lg:space-x-2">
                <div className="w-7 h-7 lg:w-8 lg:h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-blue-600" />
                </div>
                <div className="hidden sm:block text-sm">
                  <p className="font-medium text-gray-900 truncate max-w-28 lg:max-w-32">
                    {user?.full_name}
                  </p>
                </div>
              </div>
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center px-1.5 lg:px-2 py-1.5 lg:py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="h-3.5 w-3.5 lg:h-4 lg:w-4 sm:mr-1.5 lg:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-1 lg:p-2 xl:p-3">
          <div className="w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 